// src/utils/invoiceZipDownloader.js
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../config/firebase';

/**
 * Extract Firebase Storage path from public URL
 * @param {string} url - Public Firebase Storage URL
 * @returns {string|null} Storage path or null if invalid
 */
function extractStoragePathFromUrl(url) {
  try {
    // URL formats:
    // 1. https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded-path}?alt=media
    // 2. https://storage.googleapis.com/{bucket}/{path}
    const urlObj = new URL(url);

    // Check if it's the Firebase Storage API format (v0/b/...)
    if (urlObj.pathname.includes('/v0/b/')) {
      // Extract path from encoded format
      // Path format: /v0/b/{bucket}/o/{encoded-path}
      const matches = urlObj.pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)/);
      if (matches && matches[1]) {
        // Decode the path (URL encoded)
        const storagePath = decodeURIComponent(matches[1]);
        console.log(`Extracted storage path: ${storagePath} from URL: ${url.substring(0, 80)}...`);
        return storagePath;
      }
    } else {
      // Legacy format: https://storage.googleapis.com/{bucket}/{path}
      const pathParts = urlObj.pathname.split('/');
      pathParts.shift(); // Remove ''
      pathParts.shift(); // Remove bucket name
      const storagePath = pathParts.join('/');
      console.log(`Extracted storage path (legacy): ${storagePath} from URL: ${url.substring(0, 80)}...`);
      return storagePath || null;
    }

    console.error('Could not extract storage path from URL:', url);
    return null;
  } catch (error) {
    console.error('Failed to extract storage path from URL:', url, error);
    return null;
  }
}

/**
 * Sanitize filename to remove invalid characters and handle Hebrew properly
 * @param {string} name - Original filename
 * @returns {string} Sanitized filename
 */
function sanitizeFilename(name) {
  if (!name) return 'invoice';

  // Remove invalid characters for filenames
  let sanitized = name
    .replace(/[<>:"/\\|?*]/g, '_') // Replace invalid chars with underscore
    .replace(/\s+/g, '_') // Replace spaces with underscore
    .replace(/_{2,}/g, '_') // Replace multiple underscores with single
    .trim();

  // Limit length to avoid filesystem issues
  if (sanitized.length > 100) {
    sanitized = sanitized.substring(0, 100);
  }

  return sanitized || 'invoice';
}

/**
 * Fetch a file from Firebase Storage with retry logic
 * @param {string} url - Public Firebase Storage URL
 * @param {number} maxRetries - Maximum number of retries
 * @returns {Promise<Blob>} File blob
 */
async function fetchWithRetry(url, maxRetries = 3) {
  let lastError;

  // Extract storage path from URL
  const storagePath = extractStoragePathFromUrl(url);

  if (!storagePath) {
    throw new Error(`Invalid Firebase Storage URL: ${url}`);
  }

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`Fetching ${storagePath} (attempt ${attempt}/${maxRetries})`);

      // Create storage reference
      const storageRef = ref(storage, storagePath);

      // Download blob using Firebase SDK (bypasses CORS)
      const blob = await getBlob(storageRef);

      // Verify it's a PDF
      if (!blob.type.includes('pdf') && !blob.type.includes('application/octet-stream')) {
        console.warn(`Warning: Expected PDF but got ${blob.type} for ${storagePath}`);
      }

      console.log(`Successfully fetched ${storagePath}, size: ${blob.size} bytes`);
      return blob;

    } catch (error) {
      lastError = error;
      console.warn(`Attempt ${attempt} failed for ${storagePath}:`, error.message);

      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
      }
    }
  }

  throw new Error(`Failed to download invoice after ${maxRetries} attempts: ${lastError.message}`);
}

/**
 * Create a ZIP file containing all invoice PDFs
 * @param {Array} invoices - Array of invoice objects (can be grouped invoices with minDate/maxDate or legacy with appointmentDate)
 * @param {Function} onProgress - Callback for progress updates (current, total)
 * @returns {Promise<Blob>} ZIP file blob
 */
export async function createInvoiceZip(invoices, onProgress) {
  const zip = new JSZip();
  const errors = [];
  const processedUrls = new Set(); // Track processed invoice URLs to avoid duplicates

  console.log(`Starting ZIP creation for ${invoices.length} invoices`);

  for (let i = 0; i < invoices.length; i++) {
    const invoice = invoices[i];

    try {
      // Skip if we've already processed this invoice URL
      if (processedUrls.has(invoice.invoiceUrl)) {
        console.log(`Skipping duplicate invoice URL: ${invoice.invoiceUrl}`);
        continue;
      }

      // Call progress callback
      if (onProgress) {
        onProgress(i + 1, invoices.length, invoice);
      }

      // Fetch the invoice PDF
      const blob = await fetchWithRetry(invoice.invoiceUrl);

      // Mark this URL as processed
      processedUrls.add(invoice.invoiceUrl);

      // Handle both grouped invoices and legacy single invoices
      const date = invoice.minDate || invoice.appointmentDate || 'unknown_date';
      const customer = sanitizeFilename(invoice.customerName || 'unknown');

      // For grouped invoices, use first service name or indicate batch payment
      let serviceLabel;
      if (invoice.isBatch && invoice.serviceNames) {
        // For batch payments, use first service or indicate it's a batch
        const firstService = invoice.serviceNames.split(',')[0].trim();
        serviceLabel = sanitizeFilename(`${firstService}_batch_${invoice.appointmentCount}`);
      } else {
        serviceLabel = sanitizeFilename(invoice.serviceNames || invoice.serviceName || 'service');
      }

      let filename = `${date}_${customer}_${serviceLabel}.pdf`;

      // Add to ZIP
      zip.file(filename, blob);

      console.log(`Added to ZIP: ${filename}`);

    } catch (error) {
      console.error(`Failed to download invoice for ${invoice.customerName}:`, error);
      errors.push({
        invoice,
        error: error.message
      });
    }
  }

  console.log(`ZIP creation complete. Success: ${processedUrls.size}, Failed: ${errors.length}`);

  // Generate the ZIP file
  const zipBlob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
    compressionOptions: {
      level: 6 // Balance between speed and compression
    }
  });

  return {
    blob: zipBlob,
    errors,
    successCount: processedUrls.size,
    totalCount: invoices.length
  };
}

/**
 * Download a ZIP file to user's download folder
 * @param {Blob} blob - ZIP file blob
 * @param {string} startDate - Start date in YYYY-MM-DD format
 * @param {string} endDate - End date in YYYY-MM-DD format
 */
export function downloadZipFile(blob, startDate, endDate) {
  const filename = `Invoices_${startDate}_to_${endDate}.zip`;
  saveAs(blob, filename);
  console.log(`Downloading ${filename}`);
}

/**
 * Main function to download all invoices as a ZIP file
 * @param {Array} invoices - Array of invoice objects
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Object>} Result with success/error info
 */
export async function downloadInvoicesAsZip(invoices, startDate, endDate, onProgress) {
  try {
    console.log(`Starting batch download of ${invoices.length} invoices`);

    // Validate inputs
    if (!invoices || invoices.length === 0) {
      throw new Error('No invoices to download');
    }

    // Create ZIP
    const result = await createInvoiceZip(invoices, onProgress);

    // Download ZIP file
    downloadZipFile(result.blob, startDate, endDate);

    return {
      success: true,
      successCount: result.successCount,
      totalCount: result.totalCount,
      errors: result.errors
    };

  } catch (error) {
    console.error('Error downloading invoices as ZIP:', error);
    throw error;
  }
}
